import React from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useSubscription } from '../contexts/SubscriptionContext.tsx';

interface ReadOnlyBannerProps {
  /** Optional custom message; defaults to a generic read-only notice. */
  message?: string;
}

/**
 * A subtle inline notice explaining why editing controls are hidden.
 *
 * Two independent reasons produce a read-only workspace, and the message must say WHICH,
 * because the remedies are completely different: a "User" account needs an administrator,
 * whereas a lapsed subscription needs ORchestrate. The subscription reason is checked first —
 * it affects admins too. Note that no in-app remedy is offered for it: the Billing page is
 * unlinked (see App.tsx), so subscriptions are resolved with ORchestrate directly.
 */
const ReadOnlyBanner: React.FC<ReadOnlyBannerProps> = ({
  message = 'You have read-only access. Editing actions are restricted to administrators.',
}) => {
  const { isAdmin } = useAuth();
  const { canWrite } = useSubscription();

  if (!canWrite) {
    return (
      <div
        role="status"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          marginBottom: '12px',
          borderRadius: '6px',
          fontSize: '13px',
          border: '1px solid var(--danger-text, #dc2626)',
          background: 'rgba(220, 38, 38, 0.08)',
          color: 'var(--text-muted, #6b7280)',
        }}
      >
        <span aria-hidden="true">🔒</span>
        <span>
          This workspace is read-only because the ORchestrate subscription is not active.
          {' Contact ORchestrate support to restore it.'}
        </span>
      </div>
    );
  }

  if (isAdmin) return null;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 12px',
        marginBottom: '12px',
        borderRadius: '6px',
        fontSize: '13px',
        border: '1px solid var(--amber, #d97706)',
        background: 'rgba(217, 119, 6, 0.08)',
        color: 'var(--text-muted, #6b7280)',
      }}
    >
      <span aria-hidden="true">🔒</span>
      <span>{message}</span>
    </div>
  );
};

export default ReadOnlyBanner;
